import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';

export const MAIL_DRAG_MIME = 'application/x-mail-drag';

const MailDragContext = createContext(null);

export const MailDragProvider = ({ children }) => {
  const [dragging, setDragging] = useState(null);
  const [dropTargetKey, setDropTargetKey] = useState(null);
  const dropHandlerRef = useRef(null);
  const ghostRef = useRef(null);

  const clearGhost = useCallback(() => {
    if (ghostRef.current) {
      ghostRef.current.remove();
      ghostRef.current = null;
    }
  }, []);

  const beginDrag = useCallback((payload) => {
    if (!payload?.emails?.length) return;
    setDragging({
      emails: payload.emails,
      fromFolder: payload.fromFolder || 'inbox',
      label: payload.label || 'E-Mail',
      count: payload.emails.length,
      kind: payload.kind || (payload.emails.length > 1 ? 'thread' : 'message'),
      preview: payload.preview || null,
    });
  }, []);

  const endDrag = useCallback(() => {
    clearGhost();
    setDragging(null);
    setDropTargetKey(null);
  }, [clearGhost]);

  const setDragGhost = useCallback((element) => {
    clearGhost();
    ghostRef.current = element || null;
  }, [clearGhost]);

  const setDropTarget = useCallback((key) => {
    setDropTargetKey(key || null);
  }, []);

  const registerDropHandler = useCallback((fn) => {
    dropHandlerRef.current = typeof fn === 'function' ? fn : null;
    return () => {
      if (dropHandlerRef.current === fn) dropHandlerRef.current = null;
    };
  }, []);

  const dropOnTarget = useCallback(async (target) => {
    const handler = dropHandlerRef.current;
    const payload = dragging;
    clearGhost();
    setDragging(null);
    setDropTargetKey(null);
    if (!handler || !payload?.emails?.length || !target) return false;
    return handler(payload, target);
  }, [dragging, clearGhost]);

  const value = useMemo(
    () => ({
      dragging,
      isDragging: !!dragging,
      dropTargetKey,
      beginDrag,
      endDrag,
      setDragGhost,
      setDropTarget,
      registerDropHandler,
      dropOnTarget,
    }),
    [
      dragging,
      dropTargetKey,
      beginDrag,
      endDrag,
      setDragGhost,
      setDropTarget,
      registerDropHandler,
      dropOnTarget,
    ]
  );

  return <MailDragContext.Provider value={value}>{children}</MailDragContext.Provider>;
};

export const useMailDrag = () => {
  const ctx = useContext(MailDragContext);
  if (!ctx) throw new Error('useMailDrag must be used within MailDragProvider');
  return ctx;
};

export const useMailDropTarget = (targetKey, target, { enabled = true } = {}) => {
  const { isDragging, dropTargetKey, setDropTarget, dropOnTarget } = useMailDrag();
  const isOver = isDragging && dropTargetKey === targetKey && enabled;

  const onDragEnter = useCallback(
    (e) => {
      if (!enabled || !isDragging) return;
      e.preventDefault();
      e.stopPropagation();
      setDropTarget(targetKey);
    },
    [enabled, isDragging, setDropTarget, targetKey]
  );

  const onDragOver = useCallback(
    (e) => {
      if (!enabled || !isDragging) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
      if (dropTargetKey !== targetKey) setDropTarget(targetKey);
    },
    [enabled, isDragging, dropTargetKey, setDropTarget, targetKey]
  );

  const onDragLeave = useCallback(
    (e) => {
      if (!enabled) return;
      const related = e.relatedTarget;
      if (related && e.currentTarget.contains(related)) return;
      if (dropTargetKey === targetKey) setDropTarget(null);
    },
    [enabled, dropTargetKey, setDropTarget, targetKey]
  );

  const onDrop = useCallback(
    async (e) => {
      if (!enabled || !isDragging) return;
      e.preventDefault();
      e.stopPropagation();
      await dropOnTarget(target);
    },
    [enabled, isDragging, dropOnTarget, target]
  );

  return {
    isOver,
    isDragging,
    dropProps: enabled
      ? { onDragEnter, onDragOver, onDragLeave, onDrop }
      : {},
  };
};
